import React, { useContext, useState } from "react";
import { viewServerFile } from "../../../../../../legacyComponents/util/helperFunctions";
import { styles } from "../util";
import {
  Stack,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import { FormContainer, NavButtons } from "../../../../Menu/Flow";
import { UserContext } from "../../../../../../context/UserContext";
import SingleCheckbox from "../../../../../../components/SingleCheckbox";
import employerFlowStyles from "../../styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import api from "../../../../../../api/axios";
import { toast } from "react-toastify";

export default function Documents(props) {
  const { mode, exitUrl, step, formik, onboarding, displayOnly, documents } =
    props;
  const { errors, touched, values } = formik;
  const { user } = useContext(UserContext);
  const employer = user?.employerAdminEmployer;
  const employerId = employer?.id;
  let missingSectionsString = values?.missingSectionsString || "";
  missingSectionsString = "";

  let validUser = false;
  if (user.id === employer?.rootAdmin?.id) {
    validUser = true;
  } else if (user.isSysAdmin) {
    validUser = true;
  } else if (user.isPartner && employer?.partner?.id === user?.partnerId) {
    validUser = false;
  }
  //validUser = true;

  const rootAdminName = `${employer?.rootAdmin?.firstName} ${employer?.rootAdmin?.lastName}`;
  let planTypeString = "";
  const employerPlans = employer?.employerPlans || [];

  if (employerPlans?.length > 0) {
    const employerPlanTypes = [];
    for (const employerPlan of employerPlans) {
      if (!employerPlanTypes.includes(employerPlan.type)) {
        employerPlanTypes.push(employerPlan.type);
      }
    }
    planTypeString = employerPlanTypes.join(", ");
  }

  // Authorized signatory modal state
  const [signatoryModalOpen, setSignatoryModalOpen] = useState(false);
  const [signatoryForm, setSignatoryForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [signatoryErrors, setSignatoryErrors] = useState({});
  const [signatorySubmitting, setSignatorySubmitting] = useState(false);
  const [signatorySubmitted, setSignatorySubmitted] = useState(false);

  const handleSignatoryChange = (field) => (e) => {
    setSignatoryForm((prev) => ({ ...prev, [field]: e.target.value }));
    setSignatoryErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateSignatoryForm = () => {
    const errors = {};
    if (!signatoryForm.firstName.trim()) {
      errors.firstName = "First name is required";
    }
    if (!signatoryForm.lastName.trim()) {
      errors.lastName = "Last name is required";
    }
    if (!signatoryForm.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signatoryForm.email)) {
      errors.email = "Please enter a valid email";
    }
    if (!signatoryForm.phone.trim()) {
      errors.phone = "Phone number is required";
    }
    setSignatoryErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignatorySubmit = async () => {
    if (!validateSignatoryForm()) return;

    try {
      setSignatorySubmitting(true);
      const payload = {
        firstName: signatoryForm.firstName.trim(),
        lastName: signatoryForm.lastName.trim(),
        email: signatoryForm.email.trim(),
        phone: signatoryForm.phone.replace(/\D/g, ""),
      };
      await api.post(`/employer/${employerId}/signup/addAdmin`, payload);
      toast.success("Authorized signatory added successfully!");
      setSignatorySubmitted(true);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to add authorized signatory",
      );
    } finally {
      setSignatorySubmitting(false);
    }
  };

  const handleSignatoryModalClose = () => {
    setSignatoryModalOpen(false);
    if (!signatorySubmitted) {
      setSignatoryForm({ firstName: "", lastName: "", email: "", phone: "" });
      setSignatoryErrors({});
    }
  };

  const restrictedIndustriesList = [
    "Illegal or prohibited activities (illegal drugs, drug paraphernalia, unlawful transactions)",
    'Cannabis or marijuana-related businesses (growing, selling, transporting, or otherwise "touching the plant," unless explicitly approved)',
    "Adult content or adult-oriented services (pornography, prostitution, or similar activities)",
    "Payday lenders or short-term lending businesses",
    "Money services or payment businesses (money transmitters, check-cashing, currency exchange, third-party payment processors, unless explicitly approved)",
    "Crypto or virtual currency operations (crypto ATMs, virtual currency kiosks, or similar services)",
    "High-risk or restricted financial institutions (shell banks, foreign financial institutions, foreign correspondent banks)",
    "Online pawnbrokers",
  ];

  const RestrictedIndustriesTooltip = () => (
    <Tooltip
      title={
        <Stack spacing={1}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            Restricted or prohibited businesses include:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: "20px" }}>
            {restrictedIndustriesList.map((item, index) => (
              <li key={index} style={{ fontSize: "12px", marginBottom: "4px" }}>
                {item}
              </li>
            ))}
          </ul>
        </Stack>
      }
      arrow
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#fff",
            color: "#465578",
            border: "1px solid #ACBEE8",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            fontSize: "13px",
            padding: "12px 14px",
            maxWidth: 450,
            "& .MuiTooltip-arrow": {
              color: "#fff",
              "&::before": {
                border: "1px solid #ACBEE8",
                backgroundColor: "#fff",
              },
            },
          },
        },
        arrow: {
          sx: {
            color: "#fff",
            "&::before": {
              border: "1px solid #ACBEE8",
              backgroundColor: "#fff",
            },
          },
        },
      }}
    >
      <InfoOutlinedIcon
        sx={{
          fontSize: "15px",
          color: "#B0BEC5",
          cursor: "pointer",
          marginLeft: "4px",
          verticalAlign: "middle",
          "&:hover": {
            color: "#6579A8",
          },
        }}
      />
    </Tooltip>
  );

  async function viewDocument(document) {
    const filename = document?.file?.name;
    const url = document?.links?.serve?.url;
    await viewServerFile({ url, filename, download: true });
  }
  const hideNext = mode === "manage";

  /**
   * Three roles on this step, and only the first can move the plan forward.
   * The two flags below are mutually exclusive, so a non-signer sees exactly
   * one box.
   *
   * THE SIGNER (root admin, or a sys admin acting for them) gets the
   * checkboxes and Next, and no box at all.
   *
   * A PARTNER cannot sign, but can carry on with the rest of setup meanwhile,
   * so they get the dashboard link.
   *
   * ANY OTHER ADMIN cannot sign and has nowhere else to go, so they are told
   * who to fetch — and offered the chance to nominate someone if the root
   * admin is not the right person.
   */
  const needsSignature = !validUser && documents?.length > 0;
  const isPartnerUser =
    !!employer?.partner?.id && employer?.partner?.id === user?.partnerId;
  const showCompleteSetupLater = !hideNext && needsSignature && isPartnerUser;
  const showRootAdminMessage = needsSignature && !showCompleteSetupLater;

  if (!user) {
    return <div style={{ minHeight: "400px" }} />;
  }

  // Show missing sections message if there are incomplete sections
  if (missingSectionsString?.length > 0) {
    return (
      <Stack spacing="30px" height="100%">
        <FormContainer>
          <Typography variant="h2" sx={employerFlowStyles.h2}>
            Documents
          </Typography>

          <Stack spacing={2} sx={{ marginTop: "40px" }}>
            <Typography sx={{ color: "#d32f2f", fontWeight: 600 }}>
              You have not completed all of the sections for setting up an
              employer plan. Please complete these sections to continue.
            </Typography>
            <Typography sx={{ color: "#d32f2f" }}>
              {missingSectionsString}
            </Typography>
          </Stack>
        </FormContainer>

        <NavButtons
          mode={mode}
          enableSave={true}
          exitUrl={exitUrl}
          step={step}
          hideNext={true}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing="30px" height="100%">
      <FormContainer>
        {/* Complete Setup Later banner — the partner */}
        {showCompleteSetupLater && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#f8f9fc",
              border: "1px solid #ACBEE8",
              borderRadius: "8px",
            }}
          >
            <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
              Complete Setup Later
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              An employer signatory must complete the remaining signatures before the HRA can become active. You can continue setup now by navigating to the Administrator Dashboard to add employees and their spouses and dependents.
            </Typography>
            <Typography
              component="a"
              href={`/employer/${employer.id}`}
              sx={{
                display: "block",
                marginTop: "8px",
                color: "#6C63FF",
                fontSize: "14px",
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": { color: "#5650d8" },
              }}
            >
              Go to Administrator Dashboard
            </Typography>
          </Box>
        )}

        {/* Not an Authorized Signatory — an admin who is not the root admin */}
        {showRootAdminMessage && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#f8f9fc",
              border: "1px solid #ACBEE8",
              borderRadius: "8px",
            }}
          >
            <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
              Not an Authorized Signatory?
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              {employer?.rootAdmin?.firstName
                ? `Only the Root Admin, ${rootAdminName}, can complete the signup process. Please contact ${employer.rootAdmin.firstName} to login and approve the documents.`
                : "Only the Root Admin can complete the signup process. Please contact them to login and approve the documents."}
            </Typography>
            <Typography
              onClick={() => setSignatoryModalOpen(true)}
              sx={{
                display: "block",
                marginTop: "8px",
                color: "#6C63FF",
                fontSize: "14px",
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": { color: "#5650d8" },
              }}
            >
              Click here to enter the authorized Signatory
            </Typography>
          </Box>
        )}

        <Typography variant="h2" sx={employerFlowStyles.h2}>
          Documents
        </Typography>

        <Stack spacing={2} sx={{ marginTop: "40px" }}>
          {documents?.length > 0 && (
            <Box>
              <Typography sx={{ marginBottom: "8px" }}>
                Review and Accept the following documents:
              </Typography>

              <Stack spacing="10px" sx={styles.documentsContainer}>
                {documents?.map((document, index) => (
                  <Stack key={index} sx={styles.document}>
                    <Typography>{document?.file?.name}</Typography>
                    <button
                      type="button"
                      onClick={async () => await viewDocument(document)}
                    >
                      View
                    </button>
                  </Stack>
                ))}
              </Stack>

              {/* Agreement checkbox */}
              {validUser && (<Box sx={{ marginTop: "16px" }}>
                <SingleCheckbox
                  formik={formik}
                  name="agreement"
                  label="I represent that I am an authorized signatory for the company, and I acknowledge and agree to the documents listed above."
                  disabled={displayOnly}
                />
                {errors.agreement && touched.agreement && (
                  <div className="error-message-fields">{errors.agreement}</div>
                )}
              </Box>
              )}
              {/* Eligible Industry Certification grouped together */}
              {validUser && (<Box sx={{ marginTop: "32px" }}>
                <Typography>
                  Salusion does not support certain prohibited or restricted
                  businesses.
                </Typography>

                <Box sx={{ marginTop: "8px" }}>
                  <SingleCheckbox
                    formik={formik}
                    name="eligibleIndustryCertification"
                    label={
                      <>
                        I represent and warrant that the company does not
                        operate in, or derive revenue from, any prohibited or
                        restricted industry as described in the restricted
                        industries list.
                        <RestrictedIndustriesTooltip />
                      </>
                    }
                    disabled={displayOnly}
                  />
                  {errors.eligibleIndustryCertification &&
                    touched.eligibleIndustryCertification && (
                      <div className="error-message-fields">
                        {errors.eligibleIndustryCertification}
                      </div>
                    )}
                </Box>
              </Box>
              )}
            </Box>
          )}

          {validUser && (!documents || documents?.length === 0) && (
            <Typography>
              Please contact Salusion for the required documents.
            </Typography>
          )}

        </Stack>
      </FormContainer>

      <NavButtons
        mode={mode}
        enableSave={true}
        exitUrl={exitUrl}
        step={step}
        hideNext={hideNext || needsSignature}
      />

      {/* Authorized Signatory Modal */}
      <Dialog
        open={signatoryModalOpen}
        onClose={handleSignatoryModalClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle sx={{ color: "#465578", fontWeight: 600 }}>
          Enter Authorized Signatory
        </DialogTitle>
        <DialogContent>
          {signatorySubmitted ? (
            <Typography sx={{ color: "#6579A8", py: 2 }}>
              The authorized signatory has been added. They will receive an
              email to complete the signup process.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="First Name"
                value={signatoryForm.firstName}
                onChange={handleSignatoryChange("firstName")}
                error={Boolean(signatoryErrors.firstName)}
                helperText={signatoryErrors.firstName}
                fullWidth
                size="small"
              />
              <TextField
                label="Last Name"
                value={signatoryForm.lastName}
                onChange={handleSignatoryChange("lastName")}
                error={Boolean(signatoryErrors.lastName)}
                helperText={signatoryErrors.lastName}
                fullWidth
                size="small"
              />
              <TextField
                label="Email"
                type="email"
                value={signatoryForm.email}
                onChange={handleSignatoryChange("email")}
                error={Boolean(signatoryErrors.email)}
                helperText={signatoryErrors.email}
                fullWidth
                size="small"
              />
              <TextField
                label="Phone"
                value={signatoryForm.phone}
                onChange={handleSignatoryChange("phone")}
                error={Boolean(signatoryErrors.phone)}
                helperText={signatoryErrors.phone}
                fullWidth
                size="small"
              />
              <Typography sx={{ color: "#6579A8", fontSize: "14px", mt: 1 }}>
                Salusion will make this person an administrator of the account
                and send them an email. When they create their account they can
                complete this process.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", gap: "20px" }}>
          {signatorySubmitted ? (
            <Button
              variant="contained"
              onClick={handleSignatoryModalClose}
              sx={{ minWidth: "108.31px !important" }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={handleSignatoryModalClose}
                sx={{ minWidth: "108.31px !important" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSignatorySubmit}
                disabled={signatorySubmitting}
                sx={{ minWidth: "108.31px !important" }}
              >
                {signatorySubmitting ? (
                  <CircularProgress
                    size={24}
                    sx={{ ".MuiCircularProgress-circle": { color: "#fff" } }}
                  />
                ) : (
                  "Submit"
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
